// ── cohort-complete-modal: the Complete cohort confirm Modal ────────────────
// Wave 3 (Cohorts). Completion is POST /cohorts/:id/complete {confirm:true}. The
// live route validates that ALL sessions are done; a too-early completion comes
// back as a SOFT refusal — `{ ok:false, status:"completion_blocked:N_of_10..." }`
// (a 200 result, NOT an exception) — so the page passes the result back here and
// we surface it honestly. A closed/blocked completion NEVER implies the cohort
// was completed (no-fabrication release gate); the dialog stays open and states
// that no change was made. A hard error (permission-denied / unavailable) reads
// via describeCohortWriteError.

import { Alert, Button, Modal } from "@vjal/ui";

import type { CoachingCohortDto, CoachingOperationResult } from "../../services/coaching-dtos";
import { describeCohortWriteError } from "./cohorts-write";

export interface CohortCompleteModalProps {
  open: boolean;
  cohort: CoachingCohortDto | null;
  onClose: () => void;
  /** The actual POST .../complete — owned by the page (react-query mutation). */
  onConfirm: () => Promise<CoachingOperationResult>;
  submitting?: boolean;
  submitError?: unknown;
  /** The last result — used to surface a SOFT (ok:false) completion block. */
  lastResult?: CoachingOperationResult | null;
}

/** Turn a soft `completion_blocked:4_of_10_sessions` status into honest copy. */
function blockedMessage(result: CoachingOperationResult | null | undefined): string | null {
  if (!result || result.ok) return null;
  const status = result.status ?? "";
  if (status.startsWith("completion_blocked")) {
    // Linear parse of "<done>_of_<total>" (sonarjs super-linear-regex class,
    // 2026-07-06). Equivalent to the old unanchored (\d+)_of_(\d+): digits
    // immediately BEFORE an "_of_" and immediately AFTER it, anywhere in the
    // status (real shape: "completion_blocked:4_of_10_sessions").
    const tokens = status.split("_");
    for (let i = 1; i + 1 < tokens.length; i++) {
      if (tokens[i] !== "of") continue;
      const prev = tokens[i - 1] ?? "";
      let start = prev.length;
      while (start > 0 && prev[start - 1]! >= "0" && prev[start - 1]! <= "9") start--;
      const next = tokens[i + 1] ?? "";
      let end = 0;
      while (end < next.length && next[end]! >= "0" && next[end]! <= "9") end++;
      if (start < prev.length && end > 0) {
        return `This cohort can't be completed yet — ${prev.slice(start)} of ${next.slice(0, end)} sessions are done. No change was made.`;
      }
    }
    return "This cohort can't be completed yet — not all sessions are done. No change was made.";
  }
  return "This cohort can't be completed yet. No change was made.";
}

export function CohortCompleteModal({
  open,
  cohort,
  onClose,
  onConfirm,
  submitting = false,
  submitError,
  lastResult
}: CohortCompleteModalProps) {
  const topError = describeCohortWriteError(submitError);
  const blocked = blockedMessage(lastResult);
  const name = cohort?.name || "this cohort";

  return (
    <Modal open={open} onClose={onClose} title="Complete cohort">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}
        {blocked && <Alert variant="warning">{blocked}</Alert>}

        <Alert variant="warning">
          Completing {name} marks the engagement finished. The server only allows this once every session is done; it
          confirms before any change is made.
        </Alert>

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={submitting}
            onClick={() => {
              void onConfirm().catch(() => {
                // A hard error surfaces via `submitError`; a soft block surfaces
                // via `lastResult`. Either way the dialog stays open.
              });
            }}
          >
            Complete cohort
          </Button>
        </div>
      </div>
    </Modal>
  );
}
